import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { apiClient, ApiConfig } from '@data';
import { getLiveAnalysisConfigKey, useEnableLiveAnalysis } from '@queries';
import { activityAtom, swingMeasuredParametersMapAtom, uiNodeBySmpIdAtom } from '@store';

import { LiveAnalysisFormValues } from './schema';

export const useSaveLiveAnalysisSettings = ({ onSuccess }: { onSuccess?: () => void }) => {
    const queryClient = useQueryClient();
    const parametersMap = useAtomValue(uiNodeBySmpIdAtom);
    const activity = useAtomValue(activityAtom);
    const enableLiveAnalysisMutation = useEnableLiveAnalysis();
    const swingMeasuredParametersMap = useAtomValue(swingMeasuredParametersMapAtom);

    const settingsMutation = useMutation({
        mutationFn: async ({
            setupParameters,
            eventParameters,
            regDelay,
            iterations,
            autoRestart,
        }: LiveAnalysisFormValues) => {
            if (!activity?.layoutId) throw new Error('No layout selected');
            const selectedSwingMeasuredParameterIds = [...setupParameters, ...eventParameters];
            const parameters = selectedSwingMeasuredParameterIds.flatMap((id) => {
                const measuredId = swingMeasuredParametersMap[parametersMap[id].parameter.smpId];
                return measuredId ? [{ id: measuredId.id }] : [];
            });
            await enableLiveAnalysisMutation.mutateAsync();
            return apiClient.put(ApiConfig.path('/api/v1/live-feedback/config'), {
                parameters,
                tareCountDown: regDelay,
                iterations: iterations ? iterations : null,
                autoRestart,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getLiveAnalysisConfigKey() });
            onSuccess?.();
        },
        onError: console.error,
    });

    return settingsMutation;
};
